import { type FC } from 'react';
import classnames from 'classnames';
import { useIntl } from 'dibs-react-intl';

import dibsCss from 'dibs-css';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { MEASUREMENT_UNITS, type MeasurementUnit } from 'dibs-intl/exports/units';

import { dimensionUnitAriaLabel } from '../pdpMessages.tsx';
import { trackPdpDimensionToggle } from '../tracking/pdpItemModuleTracking.ts';
import styles from './PdpDimensionUnitToggle.module.css';

type Props = {
    value: MeasurementUnit;
    onChange: (unit: MeasurementUnit) => void;
};

export const PdpDimensionUnitToggle: FC<Props> = ({ value, onChange }) => {
    const intl = useIntl();
    const activeIndex = MEASUREMENT_UNITS.indexOf(value);

    return (
        <div
            className={classnames(
                dibsCss.relative,
                dibsCss.flex,
                dibsCss.gap0,
                dibsCss.itemsStart,
                dibsCss.bgMoonstone,
                dibsCss.roundedXxsmall,
                dibsCss.flexShrink0
            )}
            role="group"
            aria-label={intl.formatMessage(dimensionUnitAriaLabel)}
            data-tn="dimension-unit-toggle"
        >
            <div
                aria-hidden="true"
                className={classnames(
                    styles.indicator,
                    dibsCss.absolute,
                    dibsCss.top0,
                    dibsCss.left0,
                    dibsCss.hFull,
                    dibsCss.pointerEventsNone,
                    dibsCss.bgNoir,
                    dibsCss.roundedXxsmall
                )}
                style={{ transform: `translateX(${activeIndex * 100}%)` }}
            />
            {MEASUREMENT_UNITS.map(unit => {
                const isActive = unit === value;

                return (
                    <Link
                        key={unit}
                        underline="none"
                        buyerLinkType={isActive ? 'contrastLinkNoEffect' : 'standardLinkNoEffect'}
                        ariaSelected={isActive}
                        dataTn={`dimension-unit-${unit.toLowerCase()}`}
                        className={classnames(
                            styles.option,
                            dibsCss.relative,
                            dibsCss.flex,
                            dibsCss.flexCol,
                            dibsCss.itemsCenter,
                            dibsCss.justifyCenter,
                            dibsCss.ptXxxsmall,
                            dibsCss.pxXxsmall,
                            dibsCss.pb0,
                            dibsCss.roundedXxsmall,
                            dibsCss.sassyFontSubHeaderSmallHeavy,
                            dibsCss.textCenter,
                            dibsCss.whitespaceNowrap,
                            dibsCss.cursorPointer
                        )}
                        onClick={() => {
                            if (!isActive) {
                                trackPdpDimensionToggle();
                                onChange(unit);
                            }
                        }}
                    >
                        {unit.toLowerCase()}
                    </Link>
                );
            })}
        </div>
    );
};
